// Live-rig multi-region engine (isaac V2 head+teeth). Every ACTIVE region runs
// its own trunk→geom→appr chain off the SAME live control vector
// (scene.body_rig === 'head' — the teeth/body branch shares the head's rig
// space), verts come from the per-branch rig2mesh anchored to the baked
// neutral (calibrate), and the per-branch decoder outputs are handed to ONE
// terminal OGSRenderer, which lifts every branch and presents once — so a rig
// update costs ONE present and ONE readback drain, not one per branch: the
// renderer submits every branch's lift before awaiting any of them, so the
// fences overlap (see OGSRenderer's header).
//
// Module-level engine, no React state; the caller (OGSModel) owns scene
// wiring, phase gating and the SplatMeshUpdater.

export class HeadTeethEngine {
  // branches: OGSBranch[] in manifest order (each owns its rig2mesh + decoders +
  // lifter). renderer: the terminal OGSRenderer with one source per branch;
  // onPresented(count) re-publishes the debug globals. isActive() lets the
  // caller gate work (render phase).
  // `camera` may be a THREE camera or a FUNCTION returning one. Pass a function
  // when the avatar can be transformed: appearance is view-dependent, so the
  // decoder must see the camera in the AVATAR's local frame (ENG-895), and that
  // has to be re-resolved on every decode rather than captured once.
  constructor({ branches, camera, renderer, onPresented, isActive = () => true }) {
    this.branches = branches
    this.camera = camera
    this.renderer = renderer
    this.onPresented = onPresented
    this.isActive = isActive
    this.active = Object.fromEntries(branches.map((b) => [b.name, true]))
    // The LIVE control space is the HEAD's: a bundle may mix widths (myra_v10 is
    // head 168 with body/clothes/hair at 2397), and the face rig drives the head.
    this.rigDim = (branches.find((b) => b.name === 'head') ?? branches[0])?.rigDim ?? 0
    this.lastRig = null
    // The bundle's rest configuration, learned at calibrate() — see baseRig().
    this.restRig = null
    // Idle pose, when it differs from the calibration anchor — see setIdleRig.
    this.idleRig = null
    // Baked per-pose anchoring (inference/poseAnchors.js), or null when the
    // bundle ships none — in which case every branch keeps the single row-0 fit
    // calibrate() made and this file behaves exactly as it did before.
    this.poseAnchors = null
    // Mutex + latest-wins coalescing (two overlapping ORT sequences on the
    // WebGPU EP race its buffer manager — same invariant as the isaac engine).
    this._inFlight = false
    this._pendingRig = null
    this._pendingCamera = false
    this._pendingPrecision = null
    // The part rig's latest pose, when one arrived mid-decode (see runPosed).
    this._pendingPose = null
    // Which of pose/rig the last drain served, so the other goes next (_drain).
    this._lastDrained = null
    this._warnedDim = false
  }

  /** The camera to decode against, resolved fresh (see the constructor). */
  cam() {
    return typeof this.camera === "function" ? this.camera() : this.camera
  }

  // Anchor each rig2mesh branch to its baked neutral: corr = neutral − rig2mesh(base).
  // Branches without rig2mesh keep their static neutral verts.
  //
  // `baseRig` MUST be the rig whose baked mesh IS `neutral_vertices`, because the
  // correction is only exact at the rig it was measured at. Passing zeros assumes
  // the control space rests at zero — true for a rig centred on 0, FALSE for one
  // declaring rig_range [0,1], where zero is an extreme corner the regressor never
  // saw. Measured on eyeline V10-C (whose neutral_vertices is exactly baked frame
  // 0, so the right anchor is pose-table row 0): anchoring at zeros leaves pose 0
  // off the baked mesh by p50 1.27 mm / p95 7.46 mm — 1.8% of head height, and
  // ~2x the size of a real pose change — while anchoring at row 0 is EXACT.
  //
  // Falls back to the zero rig when the caller can't supply one, which is the
  // previous behaviour for every bundle that ships no pose table.
  /** @returns {Promise<{name: string, error: string}[]>} branches whose rig was
   *  refused as not fitting this bundle's mesh — empty on a healthy character. */
  async calibrate(baseRig = null) {
    // Remember it: the anchor rig IS this control space's rest pose, so it is
    // also the right rig to DECODE at until the face graph pushes a live one.
    // Anchoring at row 0 while still kicking the first frame off at zeros left
    // eyeline V10-C rendering its opening frames at that extreme corner —
    // visibly off-neutral until the first ARKit update replaced it.
    if (baseRig && baseRig.length !== this.rigDim) {
      // Zero-PADDING it out to the model's width is the very "zero is an extreme
      // corner" bug this anchor exists to avoid — so every branch falls back to
      // its OWN reference rig (rigFor), and say so.
      console.warn(`[ogs-ht] rest rig is ${baseRig.length} controls but the model expects ${this.rigDim} — `
        + `anchoring with it, but decoding the initial pose at each branch's own reference rig`)
    } else {
      this.restRig = baseRig ? new Float32Array(baseRig) : null
    }
    const base = baseRig ?? this.zeroRig()
    // TWO passes, and the order is load-bearing. Most branches fit their own
    // rig->bundle frame during calibrate(), from their own mesh. The eyes/teeth
    // branch cannot: its mesh is not in the DNA, so there is nothing to fit
    // against. It borrows a sibling's frame — correct because every branch of a
    // bundle is baked in one space — which means at least one ordinary branch
    // must calibrate FIRST. Manifest order already puts the head first; this
    // does not depend on that holding.
    const deferred = []
    // The bundle's ONE rig->bundle frame, owned here rather than passed between
    // branches: whichever branch can fit it publishes it, and the branches that
    // cannot are handed it.
    let bundleSpace = null
    // Branches whose fit was REFUSED (OGSBranch.calibrate found a residual too
    // large to be the rig-vs-wrap gap). Returned rather than logged-and-dropped:
    // only the caller knows the slug, so only the caller can put it in the UI.
    const refused = []
    const record = (br) => { if (br.calibrationError) refused.push({ name: br.name, error: br.calibrationError }) }
    for (const br of this.branches) {
      if (!br.hasDeform) continue
      if (br.needsRigSpace) { deferred.push(br); continue }
      await br.calibrate(this.rigFor(br, base))
      record(br)
      bundleSpace ??= br.rigSpace
    }
    for (const br of deferred) {
      // No sibling frame means no shells can be placed; calibrate() still runs,
      // and the branch renders its neutral exactly as it did before — the state
      // this whole path is an improvement on, so it is a fallback, not a break.
      if (!bundleSpace) console.warn(`[ogs-ht] branch '${br.name}': no sibling branch published a rig space — eyes/teeth stay at neutral`)
      await br.calibrate(this.rigFor(br, base), bundleSpace)
      record(br)
    }
    return refused
  }

  /** The IDLE rig — what to decode at with no live drive.
   *
   *  Separate from the calibration anchor, because they answer different
   *  questions. The anchor must be the pose `neutral_vertices` was baked at
   *  (row 0) or the geometry lands wrong; the idle pose only has to LOOK like the
   *  face at rest, and row 0's mouth is slightly open. The caller may hand us a
   *  grafted closed-mouth version (rigBoard.graftClosedMouth) — the same vector
   *  the ARKit path composes onto, so the initial decode and the first live frame
   *  agree instead of popping. */
  setIdleRig(rig) {
    this.idleRig = rig ? new Float32Array(rig) : null
  }

  /** The rig to decode at with no live drive: the idle pose when one was set,
   *  else the bundle's rest configuration, else all-zeros (correct for a space
   *  centred on 0). */
  baseRig() {
    if (this.idleRig) return new Float32Array(this.idleRig)
    return this.restRig ? new Float32Array(this.restRig) : this.zeroRig()
  }

  zeroRig() {
    const head = this.branches.find((b) => b.name === 'head') ?? this.branches[0]
    return head.neutralRig()
  }

  /** The rig THIS branch decodes at, given the live one.
   *
   *  A branch whose control space is a DIFFERENT WIDTH is not in this vector's
   *  space at all — myra_v10's body/clothes/hair are 2397-wide while its head is
   *  a 168-control MetaHuman board — so it gets its own reference rig (row 0 of
   *  its rig params, the pose its `neutral_vertices` were exported at). Padding
   *  the live vector out to its width instead is the "zero is an extreme corner"
   *  bug the reference rig exists to avoid: for a board declaring [0, 1] the zero
   *  vector is a pose the decoders never saw, not a neutral. */
  rigFor(br, rig) {
    if (!rig || rig.length !== this.rigDim) return br.neutralRig()
    return br.rigDim === this.rigDim ? rig : br.neutralRig()
  }

  /** Whether this branch follows the live rig at all. A branch in its own control
   *  space is STATIC: it decodes once, at its own reference rig, and every later
   *  rig update reuses those outputs (its pose cannot have changed, and a
   *  re-decode of a 768² clothes branch per lip-sync frame is the whole frame
   *  budget). Camera moves still recolor it — that goes through runAppearance. */
  isLive(br) {
    return br.rigDim === this.rigDim
  }

  // Blueprint hook: {region: true} from activeBranchRegions. Unknown regions are
  // ignored. A change after the first present re-renders live; before it, the
  // initial runFull picks the active set up on its own (no early double decode).
  setRegions(regions) {
    let changed = false
    for (const br of this.branches) {
      const on = !!regions[br.name]
      if (this.active[br.name] !== on) { this.active[br.name] = on; changed = true }
    }
    if (changed && this.lastRig) this.runFull(this.lastRig)
  }

  activeBranches() {
    return this.branches.filter((b) => this.active[b.name])
  }

  /** Install the bundle's baked per-pose anchors, or null to go back to the one
   *  fit calibrate() made. MUST run after calibrate(): the deltas compose onto
   *  each branch's own row-0 anchor, which does not exist until then. */
  setPoseAnchors(blender) {
    this.poseAnchors = blender ?? null
  }

  /** Locate `rig` among the trained poses and re-place every branch by the
   *  blended delta. One blend for the whole bundle — see OGSBranch's
   *  setPoseAnchorDelta for why it is not per branch.
   *
   *  Applied to INACTIVE branches too. A region toggled off still holds its
   *  transform, and skipping it here would leave it a pose behind on the frame
   *  it is switched back on. */
  _applyPoseAnchors(rig) {
    const pa = this.poseAnchors
    if (!pa) return
    let changed
    try {
      changed = pa.update(rig)
    } catch (e) {
      // A blend that throws would otherwise take the whole decode down. Drop back
      // to the row-0 anchor — the state this feature improves on — loudly, once.
      if (!this._warnedPoseAnchor) {
        this._warnedPoseAnchor = true
        console.error('[ogs-ht] per-pose anchoring failed — falling back to the neutral anchor', e)
      }
      this.poseAnchors = null
      for (const br of this.branches) br.setPoseAnchorDelta?.([1, 0, 0, 0, 1, 0, 0, 0, 1], [0, 0, 0])
      return
    }
    if (!changed && this._poseAnchorsApplied) return
    for (const br of this.branches) br.setPoseAnchorDelta?.(pa.lin, pa.offset)
    this._poseAnchorsApplied = true
  }

  // Full pipeline at `rig`: per active branch — rig2mesh verts → trunk→geom →
  // appr — then ONE renderer pass (lift every branch, present once).
  // Serialized; latest rig wins.
  async runFull(rig) {
    if (rig && rig.length !== this.rigDim) {
      // Foreign rig push (e.g. the 60-dim isaac ARKit gather) — not this model's
      // control space. Ignore rather than decode garbage; warn once.
      if (!this._warnedDim) {
        this._warnedDim = true
        console.warn(`[ogs-ht] ignoring rig of dim ${rig.length} (model expects ${this.rigDim}) — no ARKit gather for this control space yet`)
      }
      return
    }
    if (this._inFlight) { this._pendingRig = rig; return }
    this._inFlight = true
    try {
      this.lastRig = rig
      // BEFORE the poses below: the delta is part of the transform pose() applies,
      // so installing it after would render this frame in the previous frame's
      // frame. Inside the mutex for the same reason — a coalesced rig must not
      // move the anchor out from under a decode already running.
      if (rig) this._applyPoseAnchors(rig)
      // A body pose waiting on the same mutex is applied in THIS pass instead of getting
      // one of its own. Every pass is serialized and _drain alternates between a pending
      // pose and a pending rig, so a walk under a live face animation — both pending every
      // frame — posed the body on every OTHER pass. Measured on myragaussainsplatting: the
      // body landed at 28.8 Hz against a 76 fps render and a root that moved every frame,
      // so the root travelled ~50 mm between landings while the legs held still. That is
      // the skate, and it is also why MAX_POSE_LEAD_S stopped biting: its 50 ms ceiling
      // sits above a 36 ms landing gap. The two passes compose — the pose writes verts and
      // anchors, the decode reads the rig — so serving both here costs one lift and one
      // present rather than two of each.
      const pendingPose = this._pendingPose
      if (pendingPose) {
        this._pendingPose = null
        // The live branches it re-anchors need no reanchor(): the loop below re-poses
        // every branch that has a deform, which applies the anchor this just moved.
        pendingPose()
      }
      const inbound = {}
      for (const br of this.branches) {
        if (!this.active[br.name]) continue
        if (!this.isActive()) return
        // A static branch (its own control space) keeps whatever it last decoded:
        // the rig it answers to has not changed, so re-running its trunk→geom→appr
        // would cost the frame and produce the same UVs. It still has to be HANDED
        // to the renderer — a source that supplies nothing has its packed slots
        // zeroed (PackedConcat), which would make the body vanish on the first
        // lip-sync frame.
        const cached = this.isLive(br) ? null : br.uvOutputs()
        if (cached) { this._collect(inbound, br, cached); continue }
        const branchRig = this.rigFor(br, rig)
        if (br.hasDeform) await br.pose(branchRig)
        this._collect(inbound, br, await br.renderFull(branchRig, this.cam()))
      }
      await this._present(inbound)
    } finally {
      this._inFlight = false
      this._drain()
    }
  }

  /** Re-present at externally posed VERTICES — the part rig walking the body — reusing
   *  every branch's cached decoder outputs. Body motion changes geometry, not appearance,
   *  so a frame here costs jacobians and one lift, and no ORT at all for the branches
   *  that carry a part.
   *
   *  `pose()` is the caller's: it mutates branch verts / anchors for this instant (see
   *  applyPartRigPose) and returns the branches whose ANCHOR moved. Those are the live
   *  ones, whose verts come from their own deform — the anchor only reaches the lifter
   *  through a pose, so they are re-posed here at the rig they are already showing.
   *
   *  Serialized on the same mutex as a rig decode: two writers to one branch's verts in
   *  one frame present a torn pose. Latest pose wins, exactly as the latest rig does. */
  async runPosed(pose) {
    if (this._inFlight) { this._pendingPose = pose; return }
    this._inFlight = true
    try {
      const anchored = pose() ?? []
      for (const br of anchored) {
        if (!this.active[br.name] || !br.hasDeform) continue
        if (!this.isActive()) return
        // reanchor, not pose: only the anchor moved (the head rides the body's head
        // bone), and the deform would recompute the vertices it produced last time for
        // the same face rig. It falls through to a full pose the moment that rig moves.
        await br.reanchor(this.rigFor(br, this.lastRig))
      }
      const inbound = {}
      for (const br of this.branches) {
        if (!this.active[br.name]) continue
        if (!this.isActive()) return
        // A branch that has never decoded has no cached UVs to re-lift — decode it
        // once rather than presenting a source that supplies nothing (PackedConcat
        // zeroes those slots, which is the body vanishing mid-walk).
        this._collect(inbound, br, br.uvOutputs()
          ?? await br.renderFull(this.rigFor(br, this.lastRig), this.cam()))
      }
      await this._present(inbound)
    } finally {
      this._inFlight = false
      this._drain()
    }
  }

  // Camera-only recolor: reuse each branch's cached geom, re-run appr. Branches
  // never decoded yet (enabled later) fall back to a full decode.
  async runAppearance() {
    if (!this.lastRig) return // nothing decoded yet — the initial runFull is pending
    if (this._inFlight) { this._pendingCamera = true; return }
    this._inFlight = true
    try {
      const inbound = {}
      for (const br of this.branches) {
        if (!this.active[br.name]) continue
        if (!this.isActive()) return
        this._collect(inbound, br, br.uvOutputs()
          ? await br.renderAppearance(this.cam())
          : await br.renderFull(this.rigFor(br, this.lastRig), this.cam()))
      }
      await this._present(inbound)
    } finally {
      this._inFlight = false
      this._drain()
    }
  }

  // Live fp16<->fp32 swap. Rebuilding a decoder swaps the session ref out from
  // under an in-flight trunk→geom→appr sequence, so it goes through the SAME
  // mutex as a decode and replays the current rig afterwards.
  async setPrecision(preferFp16) {
    if (this._inFlight) { this._pendingPrecision = preferFp16; return }
    this._inFlight = true
    try {
      for (const br of this.branches) await br.setPrecision(preferFp16)
    } catch (e) {
      console.error('[ogs-ht] precision swap failed', e)
    } finally {
      this._inFlight = false
      this._drain()
    }
    if (this.lastRig && !this._inFlight) this.runFull(this.lastRig)
  }

  // A branch's decoder outputs, keyed by ITS renderer input ports (branchPorts).
  _collect(inbound, br, out) {
    const [geomPort, colorPort] = br.outputs.map((p) => p.name)
    inbound[geomPort] = out.geom_uv
    inbound[colorPort] = out.color_uv
  }

  // ONE terminal pass: the renderer lifts every source it got UVs for and
  // presents the concatenated set. A branch with no ports this frame is the
  // blueprint's region toggle — its packed slots are zeroed, never dropped.
  async _present(inbound) {
    await this.renderer.evaluate({ rig: null, camera: this.camera, rigEpoch: 0, cameraEpoch: 0 }, inbound)
    this.onPresented?.(this.renderer.lastCount)
  }

  _drain() {
    // A pending POSE and a pending RIG take turns. Whichever ran last yields, so
    // neither starves the other.
    //
    // Pose-first-and-return was measured starving the face outright: over a 2.5 s
    // walk, 165 of 165 pose drains jumped a rig that was already waiting, and one
    // waited 315 ms — nine lip-sync frames, DISCARDED rather than queued, because
    // `_pendingRig` is latest-wins. Rig-first would only mirror the fault: the face
    // pushes at 30 Hz and a decode fills most of the budget, so a body clip would
    // then be the thing that never lands.
    if (this._pendingPose && this._pendingRig) {
      const takePose = this._lastDrained !== 'pose'
      this._lastDrained = takePose ? 'pose' : 'rig'
      if (takePose) {
        const p = this._pendingPose
        this._pendingPose = null
        this.runPosed(p)
      } else {
        const r = this._pendingRig
        this._pendingRig = null
        this._pendingCamera = false
        this.runFull(r)
      }
      return
    }
    if (this._pendingPose) {
      const p = this._pendingPose
      this._pendingPose = null
      this._lastDrained = 'pose'
      this.runPosed(p)
      return
    }
    if (this._pendingRig) this._lastDrained = 'rig'
    if (this._pendingPrecision != null) {
      const p = this._pendingPrecision
      this._pendingPrecision = null
      this.setPrecision(p)
    } else if (this._pendingRig) {
      const r = this._pendingRig
      this._pendingRig = null
      this._pendingCamera = false
      this.runFull(r)
    } else if (this._pendingCamera) {
      this._pendingCamera = false
      this.runAppearance()
    }
  }
}
